import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TestTube2, X, LogOut, Shield, Eye } from 'lucide-react';
import { isQAEnvironment, disableQAMode } from '@/config/firebase-qa';
import { signOut } from 'firebase/auth';
import { auth } from '@/firebase';
import { useAuthState } from '@/auth/AuthProvider';
import toast from 'react-hot-toast';

/**
 * Admin View Mode Indicator
 * 
 * Universal banner that appears when the app is in special viewing mode:
 * - QA Mode: Testing with seeded users in threadlock-qa Firebase
 * - View as User: Admin viewing production as a specific user
 * 
 * Shows connection status and provides quick actions:
 * - Return to Admin (for non-admin users or when viewing as user)
 * - Exit Mode (signs out and returns to admin state)
 */

interface ViewAsUserSession {
  originalAdminUid: string;
  originalAdminEmail: string;
  targetUid: string;
  targetEmail: string;
  targetDisplayName?: string;
  startedAt: number;
  expiresAt: number;
}

export default function QAModeIndicator() {
  const [qaMode, setQaMode] = useState(isQAEnvironment());
  const [viewAsUserSession, setViewAsUserSession] = useState<ViewAsUserSession | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuthState();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (user) {
      user.getIdTokenResult().then(result => {
        setIsAdmin(!!result.claims.isAdmin);
      });
    }
  }, [user]);

  useEffect(() => {
    // Check for view-as-user session in sessionStorage
    const sessionData = sessionStorage.getItem('admin_view_as_user');
    if (sessionData) {
      try {
        const session = JSON.parse(sessionData) as ViewAsUserSession;
        // Verify not expired
        if (session.expiresAt > Date.now()) {
          setViewAsUserSession(session);
        } else {
          sessionStorage.removeItem('admin_view_as_user');
        }
      } catch (error) {
        console.error('Failed to parse view-as-user session:', error);
        sessionStorage.removeItem('admin_view_as_user');
      }
    }

    // Listen for storage changes (if QA mode is toggled in another tab)
    const handleStorageChange = () => {
      setQaMode(isQAEnvironment());
      
      // Also check for view-as-user session changes
      const updatedSessionData = sessionStorage.getItem('admin_view_as_user');
      if (updatedSessionData) {
        setViewAsUserSession(JSON.parse(updatedSessionData));
      } else {
        setViewAsUserSession(null);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleReturnToAdmin = () => {
    const mode = viewAsUserSession ? 'view-as-user mode' : 'QA mode';
    if (confirm(`Return to admin dashboard? You will be signed out of ${mode}.`)) {
      navigate('/admin/qa');
    }
  };

  const handleDisableQAMode = async () => {
    const action = viewAsUserSession ? 'return to your admin account' : 'switch back to production Firebase';
    
    if (confirm(`${action}? You will be signed out and redirected to login.`)) {
      try {
        await signOut(auth);
        
        if (viewAsUserSession) {
          sessionStorage.removeItem('admin_view_as_user');
          toast.success('View-as-user mode ended. Redirecting to login...');
        } else {
          disableQAMode();
          toast.success('QA Mode disabled. Redirecting to login...');
        }
        
        setTimeout(() => {
          window.location.href = '/login';
        }, 500);
      } catch (error) {
        console.error('Error signing out:', error);
        toast.error('Failed to sign out');
      }
    }
  };

  // Don't show if not in QA mode AND not in view-as-user mode, or if user dismissed it
  if ((!qaMode && !viewAsUserSession) || !isVisible) {
    return null;
  }

  // Determine banner appearance based on mode
  const isViewAsUser = !!viewAsUserSession;
  const Icon = isViewAsUser ? Eye : TestTube2;
  const bgColor = isViewAsUser ? 'bg-blue-900/95' : 'bg-purple-900/95';
  const borderColor = isViewAsUser ? 'border-blue-500/50' : 'border-purple-500/50';
  const iconBgColor = isViewAsUser ? 'bg-blue-600' : 'bg-purple-600';
  const textColor = isViewAsUser ? 'text-blue-100' : 'text-purple-100';
  const subtextColor = isViewAsUser ? 'text-blue-200/80' : 'text-purple-200/80';
  const title = isViewAsUser ? 'Viewing as User' : 'QA Mode Active';
  const subtitle = isViewAsUser 
    ? `Viewing as: ${viewAsUserSession.targetEmail}` 
    : 'Connected to threadlock-qa';
  const description = isViewAsUser
    ? `Admin: ${viewAsUserSession.originalAdminEmail}`
    : 'All data is isolated from production';

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm">
      <div className={`rounded-lg border ${borderColor} ${bgColor} p-4 shadow-2xl backdrop-blur-sm`}>
        <div className="flex items-start gap-3">
          <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${iconBgColor}`}>
            <Icon className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-2">
              <h3 className={`font-bold ${textColor}`}>{title}</h3>
              <button
                onClick={() => setIsVisible(false)}
                className={`${isViewAsUser ? 'text-blue-300 hover:text-blue-100' : 'text-purple-300 hover:text-purple-100'} transition-colors`}
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className={`mt-1 text-xs ${subtextColor}`}>
              {subtitle}
            </p>
            <p className={`mt-1 text-xs ${isViewAsUser ? 'text-blue-200/60' : 'text-purple-200/60'}`}>
              {description}
            </p>
            <div className="mt-3 flex gap-2">
              {!isAdmin && (
                <button
                  onClick={handleReturnToAdmin}
                  className={`flex flex-1 items-center justify-center gap-1 rounded ${isViewAsUser ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700'} px-3 py-1.5 text-xs font-medium text-white transition-colors`}
                >
                  <Shield className="h-3 w-3" />
                  Return to Admin
                </button>
              )}
              <button
                onClick={handleDisableQAMode}
                className="flex flex-1 items-center justify-center gap-1 rounded bg-slate-700 px-3 py-1.5 text-xs font-medium text-slate-200 transition-colors hover:bg-slate-600"
              >
                <LogOut className="h-3 w-3" />
                {isViewAsUser ? 'Exit View Mode' : 'Exit QA Mode'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
